/**
 * Cliente MSAL.js para autenticação com Microsoft Entra ID.
 *
 * Estratégia:
 *  - Login via redirect (mais robusto que popup em mobile/browsers restritivos)
 *  - Silent token refresh em background
 *  - Token cache em sessionStorage (vai embora ao fechar aba)
 *
 * Uso:
 *   await initMsal();
 *   const token = await getAccessToken();   // pra mandar pro backend
 *   const account = getActiveAccount();     // pra exibir nome/email no UI
 *   await login();                           // dispara redirect
 *   await logout();
 */

import {
  PublicClientApplication,
  type AccountInfo,
  type AuthenticationResult,
  type Configuration,
  EventType,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';

const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID ?? '';
const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID ?? '';
const apiScope = import.meta.env.VITE_ENTRA_API_SCOPE ?? '';

/**
 * BYPASS DE EMERGÊNCIA — quando MSAL não é viável (eg sem internet estável no
 * avião). Pra ativar: setar `VITE_AUTH_BYPASS=true` no build OU acessar com
 * `?bypass=1` na URL (salva flag em localStorage). Pra desativar: query `?bypass=0`.
 */
function isBypassAtivo(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get('bypass') === '1') {
    window.localStorage.setItem('AUTH_BYPASS', 'true');
  } else if (params.get('bypass') === '0') {
    window.localStorage.removeItem('AUTH_BYPASS');
  }
  return (
    import.meta.env.VITE_AUTH_BYPASS === 'true' ||
    window.localStorage.getItem('AUTH_BYPASS') === 'true'
  );
}

const FAKE_ACCOUNT: AccountInfo = {
  homeAccountId: 'bypass-home',
  environment: 'bypass',
  tenantId: 'bypass',
  username: 'daniel@infomatic.com.br',
  localAccountId: 'bypass-local',
  name: 'Daniel Giatti (Bypass)',
};

const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
};

const msal = new PublicClientApplication(msalConfig);
let initialized = false;

/**
 * Inicializa MSAL e processa qualquer redirect pendente (login callback).
 * Deve ser chamado ANTES de qualquer outra operação (no entry da aplicação).
 */
export async function initMsal(): Promise<void> {
  if (initialized) return;
  if (isBypassAtivo()) {
    initialized = true;
    console.warn('[auth] BYPASS ATIVADO — usando usuário fake. Desative com ?bypass=0');
    return;
  }
  await msal.initialize();
  const response = await msal.handleRedirectPromise();
  if (response?.account) {
    msal.setActiveAccount(response.account);
  } else {
    // Restaura account ativa do cache
    const accounts = msal.getAllAccounts();
    if (accounts.length > 0 && accounts[0]) {
      msal.setActiveAccount(accounts[0]);
    }
  }

  // Atualiza account ativa quando o usuário faz login posterior
  msal.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as AuthenticationResult;
      msal.setActiveAccount(payload.account);
    }
  });

  initialized = true;
}

export function getActiveAccount(): AccountInfo | null {
  if (isBypassAtivo()) return FAKE_ACCOUNT;
  return msal.getActiveAccount();
}

export function isAuthenticated(): boolean {
  if (isBypassAtivo()) return true;
  return msal.getActiveAccount() !== null;
}

/** Dispara o fluxo de login via redirect. Retorna depois do callback. */
export async function login(): Promise<void> {
  if (isBypassAtivo()) return; // bypass: já considera logado
  await msal.loginRedirect({
    scopes: apiScope ? [apiScope, 'openid', 'profile', 'email'] : ['openid', 'profile', 'email'],
  });
}

export async function logout(): Promise<void> {
  if (isBypassAtivo()) {
    window.localStorage.removeItem('AUTH_BYPASS');
    window.location.reload();
    return;
  }
  await msal.logoutRedirect();
}

/**
 * Obtém access token pra chamar a API.
 * Tenta silent primeiro; se falhar (refresh expirado, MFA, etc), faz redirect interativo.
 */
export async function getAccessToken(): Promise<string> {
  if (isBypassAtivo()) return 'BYPASS-NO-TOKEN'; // backend detecta e usa AUTH_BYPASS_EMAIL
  if (!apiScope) {
    throw new Error('VITE_ENTRA_API_SCOPE não configurado — não consigo pedir token pra API');
  }
  const account = msal.getActiveAccount();
  if (!account) {
    throw new Error('Sem usuário logado — chame login() primeiro');
  }

  try {
    const response = await msal.acquireTokenSilent({
      scopes: [apiScope],
      account,
    });
    return response.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // Sessão expirou — redirect pro login. Quando voltar, vai pegar token novo.
      await msal.acquireTokenRedirect({ scopes: [apiScope] });
      throw new Error('Redirecionando pra renovar autenticação...');
    }
    throw err;
  }
}
